// frontend/src/modules/Properties/components/ContentBlocksTab.tsx
import { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Stack,
  Group,
  Text,
  Button,
  ActionIcon,
  Modal,
  TextInput,
  Tabs,
  Badge,
  Tooltip,
  Paper,
  Alert,
  Loader,
  Center,
  Box,
  ThemeIcon,
  ColorInput,
  SimpleGrid,
  UnstyledButton
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { modals } from '@mantine/modals';
import {
  IconPlus,
  IconEdit,
  IconTrash,
  IconGripVertical,
  IconLanguage,
  IconAlertCircle,
  IconCheck,
  IconEye,
  IconEyeOff,
  IconBlockquote,
  IconFileText,
  IconInfoCircle,
  IconStar,
  IconHeart,
  IconHome,
  IconMapPin,
  IconCalendar,
  IconClock,
  IconPhone,
  IconMail,
  IconWorld,
  IconShieldCheck,
  IconKey,
  IconTruck,
  IconSparkles,
  IconWifi,
  IconSun,
  IconCake,
  IconMoon,
  IconFlask,
  IconBulb,
  IconColorSwatch,
  IconPhoto,
  IconChartBar,
  IconBuilding,
  IconGridDots,
  IconArrowUp,
  IconArrowsMaximize,
  IconColumns,
  IconCamera,
  IconBell,
  IconLock,
  IconUsers,
  IconUser,
  IconHandStop,
  IconFlame,
  IconCash,
  IconCoffee,
  IconBolt,
  IconRefresh,
  IconScale,
  IconMusic,
  IconMovie,
  IconBook,
  IconPalette,
  IconCloud,
  IconPlane,
  IconPizza
} from '@tabler/icons-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useTranslation } from 'react-i18next';
import RichTextEditor from '@/components/RichTextEditor';
import contentBlocksApi, { ContentBlock, ContentBlockTranslation } from '@/api/contentBlocks.api';

// Language configuration
const LANGUAGES = [
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'ru', name: 'Русский', flag: '🇷🇺' },
  { code: 'zh', name: '中文', flag: '🇨🇳' },
  { code: 'th', name: 'ไทย', flag: '🇹🇭' },
  { code: 'he', name: 'עברית', flag: '🇮🇱' }
] as const;

type LanguageCode = typeof LANGUAGES[number]['code'];

// Icon configuration for content blocks
// value = HeroIcon name stored in DB (used by warm-client to render)
// icon = Tabler icon shown in admin (must visually match the HeroIcon)
// label = human-readable description
const AVAILABLE_ICONS = [
  // General
  { value: 'HiDocumentText', label: 'Document', icon: IconFileText },
  { value: 'HiInformationCircle', label: 'Info', icon: IconInfoCircle },
  { value: 'HiStar', label: 'Star', icon: IconStar },
  { value: 'HiHeart', label: 'Heart', icon: IconHeart },
  { value: 'HiSparkles', label: 'Sparkles', icon: IconSparkles },
  { value: 'HiLightBulb', label: 'Light Bulb', icon: IconBulb },
  { value: 'HiFire', label: 'Fire', icon: IconFlame },
  // Property
  { value: 'HiHome', label: 'Home', icon: IconHome },
  { value: 'HiOfficeBuilding', label: 'Building', icon: IconBuilding },
  { value: 'HiLocationMarker', label: 'Location', icon: IconMapPin },
  { value: 'HiKey', label: 'Key', icon: IconKey },
  { value: 'HiLockClosed', label: 'Lock', icon: IconLock },
  { value: 'HiShieldCheck', label: 'Shield', icon: IconShieldCheck },
  { value: 'HiCamera', label: 'Camera', icon: IconCamera },
  { value: 'HiBell', label: 'Bell', icon: IconBell },
  // Time & Contact
  { value: 'HiCalendar', label: 'Calendar', icon: IconCalendar },
  { value: 'HiClock', label: 'Clock', icon: IconClock },
  { value: 'HiPhone', label: 'Phone', icon: IconPhone },
  { value: 'HiMail', label: 'Email', icon: IconMail },
  { value: 'HiGlobe', label: 'Globe', icon: IconWorld },
  // Nature & Weather
  { value: 'HiSun', label: 'Sun', icon: IconSun },
  { value: 'HiMoon', label: 'Moon', icon: IconMoon },
  { value: 'HiCloud', label: 'Cloud', icon: IconCloud },
  { value: 'HiColorSwatch', label: 'Color Swatch', icon: IconColorSwatch },
  { value: 'HiPhotograph', label: 'Photo', icon: IconPhoto },
  { value: 'HiChartBar', label: 'Chart', icon: IconChartBar },
  // Amenities
  { value: 'HiWifi', label: 'WiFi', icon: IconWifi },
  { value: 'HiTruck', label: 'Truck', icon: IconTruck },
  { value: 'HiCake', label: 'Cake', icon: IconCake },
  { value: 'HiBeaker', label: 'Flask', icon: IconFlask },
  { value: 'HiCoffee', label: 'Coffee', icon: IconCoffee },
  { value: 'HiPizza', label: 'Food', icon: IconPizza },
  // People
  { value: 'HiUserGroup', label: 'Users', icon: IconUsers },
  { value: 'HiUser', label: 'User', icon: IconUser },
  { value: 'HiHand', label: 'Hand', icon: IconHandStop },
  // Activities
  { value: 'HiLightningBolt', label: 'Lightning', icon: IconBolt },
  { value: 'HiRefresh', label: 'Refresh', icon: IconRefresh },
  { value: 'HiScale', label: 'Scale', icon: IconScale },
  { value: 'HiMusicNote', label: 'Music', icon: IconMusic },
  { value: 'HiFilm', label: 'Film', icon: IconMovie },
  { value: 'HiBookOpen', label: 'Book', icon: IconBook },
  { value: 'HiPaperAirplane', label: 'Airplane', icon: IconPlane },
  // Layout
  { value: 'HiViewGrid', label: 'Grid', icon: IconGridDots },
  { value: 'HiArrowUp', label: 'Arrow Up', icon: IconArrowUp },
  { value: 'HiArrowsExpand', label: 'Expand', icon: IconArrowsMaximize },
  { value: 'HiViewBoards', label: 'Columns', icon: IconColumns },
  { value: 'HiColorSwatchAlt', label: 'Palette', icon: IconPalette },
  // Money
  { value: 'HiBeaker2', label: 'Cash', icon: IconCash },
] as const;

// Color presets for icons
const COLOR_PRESETS = [
  '#f97316', // orange (default)
  '#ef4444', // red
  '#f59e0b', // amber
  '#eab308', // yellow
  '#84cc16', // lime
  '#22c55e', // green
  '#10b981', // emerald
  '#14b8a6', // teal
  '#06b6d4', // cyan
  '#0ea5e9', // sky
  '#3b82f6', // blue
  '#6366f1', // indigo
  '#8b5cf6', // violet
  '#a855f7', // purple
  '#d946ef', // fuchsia
  '#ec4899', // pink
  '#f43f5e', // rose
  '#6b7280', // gray
  '#1f2937', // dark
];

// Get icon component by value
const getIconComponent = (iconValue: string) => {
  const found = AVAILABLE_ICONS.find(i => i.value === iconValue);
  return found?.icon || IconFileText;
};


// Sortable Block Item Component
interface SortableBlockItemProps {
  block: ContentBlock;
  onEdit: (block: ContentBlock) => void;
  onDelete: (blockId: number) => void;
  onToggleActive: (blockId: number, isActive: boolean) => void;
  currentLang: string;
}

function SortableBlockItem({ block, onEdit, onDelete, onToggleActive, currentLang }: SortableBlockItemProps) {
  const { t } = useTranslation();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: block.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1
  };

  const langKey = currentLang as LanguageCode;
  const title = block.translations[langKey]?.title || block.translations.en?.title || t('properties.contentBlocks.untitled');
  const hasContent = Object.values(block.translations).some(t => t?.content && t.content.length > 0);
  const filledLanguages = LANGUAGES.filter(
    lang => block.translations[lang.code as LanguageCode]?.title
  );

  const IconComponent = getIconComponent(block.icon);

  return (
    <Paper
      ref={setNodeRef}
      style={style}
      shadow="xs"
      p="md"
      withBorder
      className={`transition-all ${!block.is_active ? 'opacity-60' : ''}`}
    >
      <Group justify="space-between" wrap="nowrap">
        <Group wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
          <ActionIcon
            variant="subtle"
            color="gray"
            style={{ cursor: 'grab', touchAction: 'none' }}
            {...attributes}
            {...listeners}
          >
            <IconGripVertical size={18} />
          </ActionIcon>

          <ThemeIcon
            size="lg"
            radius="md"
            style={{ backgroundColor: block.icon_color || '#f97316' }}
          >
            <IconComponent size={18} color="white" />
          </ThemeIcon>

          <Box style={{ minWidth: 0, flex: 1 }}>
            <Group gap="xs" mb={4}>
              <Text fw={500} truncate style={{ maxWidth: 300 }}>
                {title}
              </Text>
              {!block.is_active && (
                <Badge size="xs" color="gray">{t('properties.contentBlocks.hidden')}</Badge>
              )}
            </Group>
            <Group gap={4}>
              {filledLanguages.map(lang => (
                <Tooltip key={lang.code} label={lang.name}>
                  <Badge size="xs" variant="light" color="blue">
                    {lang.flag}
                  </Badge>
                </Tooltip>
              ))}
              {!hasContent && (
                <Badge size="xs" color="orange" variant="light">
                  {t('properties.contentBlocks.noContent')}
                </Badge>
              )}
            </Group>
          </Box>
        </Group>

        <Group gap="xs" wrap="nowrap">
          <Tooltip label={block.is_active ? t('properties.contentBlocks.hide') : t('properties.contentBlocks.show')}>
            <ActionIcon
              variant="subtle"
              color={block.is_active ? 'green' : 'gray'}
              onClick={() => onToggleActive(block.id, !block.is_active)}
            >
              {block.is_active ? <IconEye size={18} /> : <IconEyeOff size={18} />}
            </ActionIcon>
          </Tooltip>
          <Tooltip label={t('common.edit')}>
            <ActionIcon variant="subtle" color="blue" onClick={() => onEdit(block)}>
              <IconEdit size={18} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label={t('common.delete')}>
            <ActionIcon variant="subtle" color="red" onClick={() => onDelete(block.id)}>
              <IconTrash size={18} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Group>
    </Paper>
  );
}

// Main Component
interface ContentBlocksTabProps {
  propertyId: number;
  isViewMode?: boolean;
}

export default function ContentBlocksTab({ propertyId, isViewMode = false }: ContentBlocksTabProps) {
  const { t, i18n } = useTranslation();
  const [blocks, setBlocks] = useState<ContentBlock[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [modalOpened, { open: openModal, close: closeModal }] = useDisclosure(false);
  const [editingBlock, setEditingBlock] = useState<ContentBlock | null>(null);
  const [activeTab, setActiveTab] = useState<string>('en');
  const [translations, setTranslations] = useState<Record<LanguageCode, ContentBlockTranslation>>({
    en: { title: '', content: '' },
    ru: { title: '', content: '' },
    zh: { title: '', content: '' },
    th: { title: '', content: '' },
    he: { title: '', content: '' }
  });
  const [selectedIcon, setSelectedIcon] = useState<string>('HiDocumentText');
  const [selectedColor, setSelectedColor] = useState<string>('#f97316');

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // Load blocks
  const loadBlocks = useCallback(async () => {
    if (!propertyId) return;
    try {
      setLoading(true);
      const data = await contentBlocksApi.getPropertyBlocks(propertyId);
      setBlocks(data);
    } catch (error) {
      console.error('Failed to load content blocks:', error);
      notifications.show({
        title: t('common.error'),
        message: t('properties.contentBlocks.loadError'),
        color: 'red'
      });
    } finally {
      setLoading(false);
    }
  }, [propertyId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadBlocks();
  }, [propertyId]); // Only reload when propertyId changes

  // Handle drag end
  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = blocks.findIndex(b => b.id === active.id);
      const newIndex = blocks.findIndex(b => b.id === over.id);
      const newBlocks = arrayMove(blocks, oldIndex, newIndex);
      setBlocks(newBlocks);

      try {
        await contentBlocksApi.reorderBlocks(propertyId, newBlocks.map(b => b.id));
      } catch (error) {
        console.error('Failed to reorder blocks:', error);
        loadBlocks(); // Revert on error
      }
    }
  };

  // Open modal for new block
  const handleAddBlock = () => {
    setEditingBlock(null);
    setTranslations({
      en: { title: '', content: '' },
      ru: { title: '', content: '' },
      zh: { title: '', content: '' },
      th: { title: '', content: '' },
      he: { title: '', content: '' }
    });
    setSelectedIcon('HiDocumentText');
    setSelectedColor('#f97316');
    setActiveTab('en');
    openModal();
  };

  // Open modal for editing
  const handleEditBlock = (block: ContentBlock) => {
    setEditingBlock(block);
    setTranslations({
      en: block.translations.en || { title: '', content: '' },
      ru: block.translations.ru || { title: '', content: '' },
      zh: block.translations.zh || { title: '', content: '' },
      th: block.translations.th || { title: '', content: '' },
      he: block.translations.he || { title: '', content: '' }
    });
    setSelectedIcon(block.icon || 'HiDocumentText');
    setSelectedColor(block.icon_color || '#f97316');
    setActiveTab('en');
    openModal();
  };

  // Save block
  const handleSaveBlock = async () => {
    // Validate at least one title
    const hasTitle = Object.values(translations).some(t => t.title.trim());
    if (!hasTitle) {
      notifications.show({
        title: t('common.error'),
        message: t('properties.contentBlocks.titleRequired'),
        color: 'red'
      });
      return;
    }

    try {
      setSaving(true);
      if (editingBlock) {
        await contentBlocksApi.updateBlock(propertyId, editingBlock.id, {
          translations,
          icon: selectedIcon,
          icon_color: selectedColor
        });
        notifications.show({
          title: t('common.success'),
          message: t('properties.contentBlocks.updated'),
          color: 'green',
          icon: <IconCheck size={18} />
        });
      } else {
        await contentBlocksApi.createBlock(propertyId, {
          translations,
          icon: selectedIcon,
          icon_color: selectedColor
        });
        notifications.show({
          title: t('common.success'),
          message: t('properties.contentBlocks.created'),
          color: 'green',
          icon: <IconCheck size={18} />
        });
      }
      closeModal();
      loadBlocks();
    } catch (error) {
      console.error('Failed to save block:', error);
      notifications.show({
        title: t('common.error'),
        message: t('properties.contentBlocks.saveError'),
        color: 'red'
      });
    } finally {
      setSaving(false);
    }
  };

  // Delete block
  const handleDeleteBlock = (blockId: number) => {
    modals.openConfirmModal({
      title: t('properties.contentBlocks.deleteTitle'),
      children: <Text size="sm">{t('properties.contentBlocks.deleteConfirm')}</Text>,
      labels: { confirm: t('common.delete'), cancel: t('common.cancel') },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          await contentBlocksApi.deleteBlock(propertyId, blockId);
          setBlocks(prev => prev.filter(b => b.id !== blockId));
          notifications.show({
            title: t('common.success'),
            message: t('properties.contentBlocks.deleted'),
            color: 'green'
          });
        } catch (error) {
          console.error('Failed to delete block:', error);
          notifications.show({
            title: t('common.error'),
            message: t('properties.contentBlocks.deleteError'),
            color: 'red'
          });
        }
      }
    });
  };

  // Toggle block visibility
  const handleToggleActive = async (blockId: number, isActive: boolean) => {
    try {
      await contentBlocksApi.updateBlock(propertyId, blockId, { is_active: isActive });
      setBlocks(prev => prev.map(b => b.id === blockId ? { ...b, is_active: isActive } : b));
    } catch (error) {
      console.error('Failed to toggle block:', error);
    }
  };

  // Update translation field
  const updateTranslation = (lang: LanguageCode, field: 'title' | 'content', value: string) => {
    setTranslations(prev => ({
      ...prev,
      [lang]: { ...prev[lang], [field]: value }
    }));
  };

  if (loading) {
    return (
      <Center h={200}>
        <Loader />
      </Center>
    );
  }

  return (
    <Card shadow="sm" padding="lg" radius="md" withBorder>
      <Stack gap="md">
        <Group justify="space-between">
          <Group>
            <ThemeIcon size="lg" radius="md" variant="light" color="violet">
              <IconBlockquote size={20} />
            </ThemeIcon>
            <div>
              <Text fw={500}>{t('properties.contentBlocks.title')}</Text>
              <Text size="xs" c="dimmed">{t('properties.contentBlocks.description')}</Text>
            </div>
          </Group>
          {!isViewMode && (
            <Button leftSection={<IconPlus size={16} />} onClick={handleAddBlock}>
              {t('properties.contentBlocks.addBlock')}
            </Button>
          )}
        </Group>

        {blocks.length === 0 ? (
          <Alert icon={<IconAlertCircle size={16} />} color="gray">
            {t('properties.contentBlocks.empty')}
          </Alert>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext items={blocks.map(b => b.id)} strategy={verticalListSortingStrategy}>
              <Stack gap="sm">
                {blocks.map(block => (
                  <SortableBlockItem
                    key={block.id}
                    block={block}
                    onEdit={handleEditBlock}
                    onDelete={handleDeleteBlock}
                    onToggleActive={handleToggleActive}
                    currentLang={i18n.language}
                  />
                ))}
              </Stack>
            </SortableContext>
          </DndContext>
        )}
      </Stack>

      {/* Edit Modal */}
      <Modal
        opened={modalOpened}
        onClose={closeModal}
        title={
          <Group>
            <IconLanguage size={20} />
            <Text>{editingBlock ? t('properties.contentBlocks.editBlock') : t('properties.contentBlocks.newBlock')}</Text>
          </Group>
        }
        size="xl"
        closeOnClickOutside={false}
      >
        <Stack gap="md">
          {/* Icon and Color Selection */}
          <Paper withBorder p="md" radius="md">
            <Stack gap="md">
              <Group align="flex-start">
                {/* Preview */}
                <Box>
                  <Text size="sm" fw={500} mb="xs">{t('properties.contentBlocks.preview')}</Text>
                  <ThemeIcon
                    size={48}
                    radius="md"
                    style={{ backgroundColor: selectedColor }}
                  >
                    {(() => {
                      const PreviewIcon = getIconComponent(selectedIcon);
                      return <PreviewIcon size={24} color="white" />;
                    })()}
                  </ThemeIcon>
                </Box>

                {/* Color Selection */}
                <Box style={{ flex: 1 }}>
                  <Text size="sm" fw={500} mb="xs">{t('properties.contentBlocks.iconColor')}</Text>
                  <Group gap="xs" mb="xs">
                    {COLOR_PRESETS.map(color => (
                      <Tooltip key={color} label={color}>
                        <UnstyledButton
                          onClick={() => setSelectedColor(color)}
                          style={{
                            width: 24,
                            height: 24,
                            borderRadius: 4,
                            backgroundColor: color,
                            border: selectedColor === color ? '2px solid #228be6' : '1px solid #dee2e6',
                            cursor: 'pointer'
                          }}
                        />
                      </Tooltip>
                    ))}
                  </Group>
                  <ColorInput
                    size="xs"
                    value={selectedColor}
                    onChange={setSelectedColor}
                    format="hex"
                    swatches={COLOR_PRESETS}
                    withEyeDropper={false}
                  />
                </Box>
              </Group>

              {/* Icon Selection */}
              <Box>
                <Text size="sm" fw={500} mb="xs">{t('properties.contentBlocks.selectIcon')}</Text>
                <SimpleGrid cols={{ base: 6, sm: 8, md: 10 }} spacing="xs">
                  {AVAILABLE_ICONS.map(iconItem => {
                    const IconComp = iconItem.icon;
                    return (
                      <Tooltip key={iconItem.value} label={iconItem.label}>
                        <UnstyledButton
                          onClick={() => setSelectedIcon(iconItem.value)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 36,
                            height: 36,
                            borderRadius: 6,
                            backgroundColor: selectedIcon === iconItem.value ? selectedColor : '#f1f3f5',
                            border: selectedIcon === iconItem.value ? 'none' : '1px solid #dee2e6',
                            cursor: 'pointer',
                            transition: 'all 0.15s ease'
                          }}
                        >
                          <IconComp
                            size={20}
                            color={selectedIcon === iconItem.value ? 'white' : '#495057'}
                          />
                        </UnstyledButton>
                      </Tooltip>
                    );
                  })}
                </SimpleGrid>
              </Box>
            </Stack>
          </Paper>

          <Tabs value={activeTab} onChange={(v) => setActiveTab(v || 'en')}>
            <Tabs.List>
              {LANGUAGES.map(lang => (
                <Tabs.Tab
                  key={lang.code}
                  value={lang.code}
                  leftSection={<span>{lang.flag}</span>}
                  rightSection={
                    translations[lang.code as LanguageCode]?.title ? (
                      <Badge size="xs" color="green" variant="filled">
                        <IconCheck size={10} />
                      </Badge>
                    ) : null
                  }
                >
                  {lang.name}
                </Tabs.Tab>
              ))}
            </Tabs.List>

            {LANGUAGES.map(lang => (
              <Tabs.Panel key={lang.code} value={lang.code} pt="md">
                <Stack gap="md">
                  <TextInput
                    label={t('properties.contentBlocks.blockTitle')}
                    placeholder={t('properties.contentBlocks.titlePlaceholder')}
                    value={translations[lang.code as LanguageCode]?.title || ''}
                    onChange={(e) => updateTranslation(lang.code as LanguageCode, 'title', e.target.value)}
                    description={t('properties.contentBlocks.titleHint')}
                  />

                  <div>
                    <Text size="sm" fw={500} mb={4}>{t('properties.contentBlocks.blockContent')}</Text>
                    <Text size="xs" c="dimmed" mb="xs">{t('properties.contentBlocks.contentHint')}</Text>
                    <RichTextEditor
                      value={translations[lang.code as LanguageCode]?.content || ''}
                      onChange={(value) => updateTranslation(lang.code as LanguageCode, 'content', value)}
                      placeholder={t('properties.contentBlocks.contentPlaceholder')}
                    />
                  </div>
                </Stack>
              </Tabs.Panel>
            ))}
          </Tabs>

          <Group justify="flex-end" mt="md">
            <Button variant="subtle" onClick={closeModal}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleSaveBlock} loading={saving}>
              {t('common.save')}
            </Button>
          </Group>
        </Stack>
      </Modal>

    </Card>
  );
}
